import React from 'react'
import LayoutFull from '../../layouts/layoutFull/LayoutFull';
import { NAME_PROJECT, URL_BASE, URL_UPLOADED_PICTURES_FOLDER } from "../../settings/global";
import { createFetchAsync } from "../../helpers/fetch/fetchRequestAsync";
import { JSONLD, SeoCommon, SeoOpenGraph } from "../../helpers/seo/seo";
import { convertEditorStateToHTML, getPlainTextFromContent } from "../../components/draftEditor/export/exports";
import { toISO } from "../../helpers/common";
import { API_ARTICLES_BLOG_LIST, generateArticleSchema } from "../../settings/content/articles";
import Breadcrumb, { BreadcrumbLayer } from "../../components/Breadcrumb";
import Page, { Block, Content } from "../../layouts/Content";
import styles from "./[slug].module.scss";
import ImgCDN from "../../components/ImgCDN";
import DraftHTML from "../../components/draftEditor/export/DraftHTML";
import { BLOG_STYLES, formatArticleDate } from "../../pagesComponents/blog/dataHelpers";
import { useRouter } from "next/router";
import Head from "next/head";
import { NAV_BLOG } from "../../settings/navigation";
import { API_ARTICLES_INTERNAL } from "../../settings/endpoints/app";

export async function getStaticPaths() {

    const res = await fetch(API_ARTICLES_BLOG_LIST);
    const posts = await res.json();
    let articles = posts.response;

    let paths = [];

    articles.map(article => {
        if (!!article.slug) paths.push({params: {slug: article.slug}})
    });

    return {
        paths,
        fallback: 'blocking',
    }
}

export async function getStaticProps({params}) {

    let article = null;

    const {data} = await createFetchAsync({
        URL: `${API_ARTICLES_INTERNAL}/${params.slug}`
    });

    if (!data?.response?.[0]) {
        return {props: {}, notFound: true}
    } else {
        article = data.response[0]
        article.preview_plain = getPlainTextFromContent(article.preview);
        article.content_plain = getPlainTextFromContent(article.content);
        article.content = convertEditorStateToHTML(article.content, {styles: BLOG_STYLES});
    }

    return {
        props: {
            article,
            schemaData: generateArticleSchema(article)
        }
    };
}

export default function BlogArticlePage({article, schemaData}) {

    const router = useRouter();

    let previewImagePath = null;
    let seoPreviewImagePath = null;

    if (!!article.image_preview?.source) {
        const {internal, source} = article.image_preview;
        previewImagePath = `${!!internal ? URL_UPLOADED_PICTURES_FOLDER + '/' : ''}${source}`;
        seoPreviewImagePath = `${!!internal ? `${URL_BASE}${URL_UPLOADED_PICTURES_FOLDER}/` : ''}${source}`;
    }

    const {createdDate, updatedDate} = formatArticleDate(article.created, article.updated);

    return (
        <React.Fragment>
            <SeoCommon
                tags={{
                    title: article.title,
                    description: article.description_seo,
                }}
            />
            <SeoOpenGraph
                tags={{
                    ...{
                        title: article.title,
                        // description: getPlainTextFromContent(article.preview),
                        description: article.description_seo,
                        type: 'article',
                        custom: {
                            'article:published_time': toISO(article.created),
                            'article:modified_time': toISO(article.updated),
                            'article:author': article.author,
                            'article:tag': article.author,
                        }
                    },
                    ...(!!seoPreviewImagePath ? {image: seoPreviewImagePath} : {})
                }}
            />
            <JSONLD data={schemaData} tagKey={`article-${article.slug + article.name + article._id}`}/>
            <Head>
                <link rel="canonical" href={`${URL_BASE}${NAV_BLOG}/${router.query.slug}`} key='canon'/>
            </Head>

            <LayoutFull>
                <Page backgroundColor={`var(--colorBackgroundContent)`}>
                    <Block>
                        <Breadcrumb>
                            <BreadcrumbLayer link='/' name='Home'/>
                            <BreadcrumbLayer link={NAV_BLOG} name='Blog' internal/>
                            <BreadcrumbLayer name={article.title}/>
                        </Breadcrumb>
                    </Block>
                    <Block>
                        <Content noSection className={styles.article}>
                            {
                                !!previewImagePath &&
                                <div
                                    style={{
                                        paddingBottom: (article.image_preview?.height || 1) / (article.image_preview?.width || 1.91) * 100 + '%'
                                    }}
                                    className={styles.article__imageContainer}
                                >
                                    <ImgCDN
                                        className={styles.article__imageContainer__image}
                                        src={previewImagePath}
                                        alt='Article preview image'
                                        priority={!!article.image_preview?.width && !!article.image_preview?.height}
                                        style={{
                                            objectFit: 'cover',
                                            objectPosition: 'center center',
                                        }}
                                        quality={100}
                                        sizes="(max-width: 256px) 25vw, (max-width: 384px) 50vw, (max-width: 640px) 40vw, (max-width: 750px) 50vw, (max-width: 828px) 45vw, (max-width: 1080px) 45vw, (max-width: 1200px) 38vw, (max-width: 1920px) 22vw, (max-width: 2048px) 23vw, (max-width: 3840px) 18vw, 18vw"
                                        fill={true}
                                    />
                                </div>
                            }
                            <h1 className={styles.article__title}>{article.title}</h1>
                            <div className={styles.article__time}>
                                <time
                                    className={styles.article__time__created} dateTime={toISO(article.created)}
                                >{createdDate}</time>
                                {
                                    !!updatedDate &&
                                    <time className={styles.article__time__updated} dateTime={toISO(article.updated)}>
                                        (updated {updatedDate})
                                    </time>
                                }
                            </div>
                            <p className={styles.article__author}>By {article.author || NAME_PROJECT}</p>
                            <article>
                                <DraftHTML
                                    className={`content-editor blog-article ${styles.article__draft}`}
                                    content={article.content}
                                    nav={article.config?.nav}
                                    navDepth={article.config?.navDepth}
                                />
                            </article>
                            {
                                !!article.tags?.length &&
                                <div className={styles.article__tags}>
                                    {
                                        article.tags.map((tag, index) => (
                                            <p className={styles.article__tags__tag} key={index}>
                                                {tag.charAt(0).toUpperCase() + tag.slice(1)}
                                            </p>
                                        ))
                                    }
                                </div>
                            }
                        </Content>
                    </Block>
                </Page>
            </LayoutFull>
        </React.Fragment>
    );
}